import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PaymentTypeService from '../../services/paymentType.service';
import { useTranslation } from '../../hooks/useTranslation';

const DeletedPaymentTypesList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeleted();
  }, []);

  const fetchDeleted = async () => {
    setLoading(true);
    try {
      const response = await PaymentTypeService.getTrashed();
      setPaymentTypes(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted payment types:', error);
      showSnackbar(t('paymentTypes.errorLoading') + ': ' + (error.response?.data?.message || error.message), 'error');
      setPaymentTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id) => {
    setRestoreDialog({ open: true, id });
  };

  const confirmRestore = async () => {
    try {
      await PaymentTypeService.restore(restoreDialog.id);
      showSnackbar(t('paymentTypes.restoredSuccess'));
      fetchDeleted();
    } catch (error) {
      showSnackbar(t('paymentTypes.errorRestoring'), 'error');
    } finally {
      setRestoreDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPaymentTypes = paymentTypes.filter(pt =>
    pt.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/payment-types')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('paymentTypes.deletedPaymentTypes')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('paymentTypes.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('paymentTypes.amount')}</TableCell>
                  <TableCell>{t('paymentTypes.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPaymentTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t('paymentTypes.noDeletedFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPaymentTypes.map((pt) => (
                    <TableRow key={pt.id} hover>
                      <TableCell>{pt.name}</TableCell>
                      <TableCell>{pt.monto}</TableCell>
                      <TableCell>{formatDate(pt.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('paytypes.create') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(pt.id)}
                              title={t('paymentTypes.restorePaymentType')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('paymentTypes.restorePaymentType')}</DialogTitle>
        <DialogContent>
          <Typography>{t('paymentTypes.restoreConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            {t('paymentTypes.restore')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedPaymentTypesList;
